import './globals.scss'
import type { ReactNode } from 'react'
import { useEffect } from 'react'
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteLoaderData,
} from 'react-router'
import { adminService } from '#src/admin.js'
import { pageView, setupAnalytics } from '#src/analytics.js'
import { Footer } from '#src/components/Footer.js'
import { Header } from '#src/components/Header.js'
import { NotFoundView } from '#src/components/NotFoundView.js'
import { getFooterData, getHeaderData, getSeoData } from '#src/data/data.js'
import { isPreview } from '#src/env.js'
import { languageService } from '#src/languageService.js'
import { initSentry } from '#src/sentry.js'
import { BASE_URL, seoTagsToMeta } from '#src/seo.js'
import type { Route } from './+types/root.js'

export async function loader() {
  const [headerData, footerData, seoData] = await Promise.all([
    getHeaderData(),
    getFooterData(),
    getSeoData('homePage'),
  ])

  return {
    headerData,
    footerData,
    faviconTags: seoData.site.faviconMetaTags,
    defaultSeo: seoData.page?.seo || [],
    // On the preview deployment (and local dev) loaders serve draft CMS content - show the red dot.
    draftMode: isPreview,
  }
}

// Site-wide defaults; any route exporting its own `meta` replaces these
// (routes that only add to them merge via mergeMeta - see src/seo.ts).
export function meta({ loaderData, location }: Route.MetaArgs): Route.MetaDescriptors {
  return [
    ...seoTagsToMeta(loaderData?.defaultSeo),
    { tagName: 'link', rel: 'canonical', href: BASE_URL + location.pathname },
  ]
}

export function Layout({ children }: { children: ReactNode }): ReactNode {
  const { pathname } = useLocation()
  const data = useRouteLoaderData<typeof loader>('root')

  // Language is derived from the route: /sv/... pages are Swedish, everything else English.
  const lang = languageService.languageFromPath(pathname)

  // This runs "only once, on first page load"
  useEffect(() => {
    initSentry()
    setupAnalytics()
    if (data?.draftMode && !adminService.adminMode) {
      void adminService.toggleRedDot()
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: on purpose
  }, [])

  // This runs on every "pageView"
  useEffect(() => {
    pageView({
      page_title: document.title,
      page_path: pathname,
      page_location: location.origin + pathname + location.search,
    })
  }, [pathname])

  return (
    <html lang={lang}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#ffffff" />
        <link rel="preconnect" href="https://www.datocms-assets.com" />
        {/* Fonts are otherwise discovered only after root css loads; preload the two
            weights used above the fold on every page (body/header) to skip that hop. */}
        <link
          rel="preload"
          as="font"
          type="font/woff2"
          href="/fonts/AktivGrotesk-Regular-latin.woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          as="font"
          type="font/woff2"
          href="/fonts/AktivGrotesk-Medium-latin.woff2"
          crossOrigin="anonymous"
        />
        {data?.faviconTags.map((tag, i) =>
          tag.tag === 'meta' ? (
            <meta key={i} {...tag.attributes} />
          ) : (
            <link key={i} {...tag.attributes} />
          ),
        )}
        <Meta />
        <Links />
      </head>
      <body>
        {data && <Header headerData={data.headerData} />}
        <div className="page" />
        {children}
        {data && <Footer footerData={data.footerData} />}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function App(): ReactNode {
  return <Outlet />
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps): ReactNode {
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundView />
  }

  console.error(error)
  return <NotFoundView title="Something went wrong" />
}
